import {Heading} from "@/components/tailwind/heading.tsx";
import {useAdminTranslation} from "@/i18n/hooks";
import {Divider} from "@/components/tailwind/divider.tsx";
import {SectionOrganizationDetails} from "@/pages/admin/settings/section-organization-details.tsx";
import SectionSubmissionChannel from "@/pages/admin/settings/section-submission-channel.tsx";
import SectionResignationReason from "@/pages/admin/settings/section-resignation-reason.tsx";
import SectionMobilityOptions from "@/pages/admin/settings/section-mobility-options.tsx";
import MileageRateSetting from "@/pages/admin/settings/section-mileage-rate-settings.tsx";
import SectionBillingUnitSettings from "@/pages/admin/settings/section-billing-unit-settings.tsx";
import {SectionPricingConfiguration} from "@/pages/admin/settings/section-pricing-configuration.tsx";
import {SectionMembershipFeeSettings} from "@/pages/admin/settings/section-membership-fee-settings.tsx";
import SectionNdaReminderSettings from "@/pages/admin/settings/section-nda-reminder-settings.tsx";
import SectionBirthdaySettings from "@/pages/admin/settings/section-birthday-settings.tsx";
import SectionMemberAddressPrefill from "@/pages/admin/settings/section-member-address-prefill.tsx";
import SectionSocialAccount from "@/pages/admin/settings/section-social-account.tsx";
import SectionDistricts from "@/pages/admin/settings/section-districts.tsx";
import {SectionImportSettings} from "@/pages/admin/settings/section-import-settings.tsx";
import {SectionDataCleanup} from "@/pages/admin/settings/section-data-cleanup.tsx";
import {SectionExports} from "@/pages/admin/settings/section-exports.tsx";
import {SectionAuditLog} from "@/pages/admin/settings/section-audit-log.tsx";
import {SectionEmailSendLog} from "@/pages/admin/settings/section-email-send-log.tsx";
import SectionEmailConfig from "@/pages/admin/settings/section-email-config.tsx";
import SectionEmailTemplates from "@/pages/admin/settings/section-email-templates.tsx";
import SectionPasswordPolicy from "@/pages/admin/settings/section-password-policy.tsx";
import SectionPasswordBlocklist from "@/pages/admin/settings/section-password-blocklist.tsx";
import {useAuth} from "@/context/use-auth";
import {useNavigate} from "react-router-dom";
import {useEffect, useState} from "react";
import { Building, Euro, Bell, CreditCard, Send, Calculator, AlertCircle, Cake, Upload, Car, MapPin, MapPinned, ShieldCheck, Clock, Download, ScrollText, Mail, FileText, Lock, Ban, UserMinus, PiggyBank } from "lucide-react";
import { Text } from "@/components/tailwind/text";
import { checkAdminSettings, SettingsCheckResult } from "@/services/settings-check-services";
import { getEmailCapability } from "@/services/email-config-services";
import { EMAIL_CONFIG_CHANGED_EVENT } from "@/lib/email-config-events";

export default function AdminSettingsPage() {
    const {t} = useAdminTranslation();
    const {isAdmin, token} = useAuth();
    const navigate = useNavigate();
    const [settingsCheck, setSettingsCheck] = useState<SettingsCheckResult | null>(null);
    const [emailCapabilityAvailable, setEmailCapabilityAvailable] = useState(false);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;

        function refreshCapability() {
            getEmailCapability(token)
                .then((result) => {
                    if (cancelled) return;
                    setEmailCapabilityAvailable(result?.available === true);
                })
                .catch(() => {
                    if (cancelled) return;
                    setEmailCapabilityAvailable(false);
                });
        }

        refreshCapability();
        window.addEventListener(EMAIL_CONFIG_CHANGED_EVENT, refreshCapability);
        return () => {
            cancelled = true;
            window.removeEventListener(EMAIL_CONFIG_CHANGED_EVENT, refreshCapability);
        };
    }, [token]);

    useEffect(() => {
        if (!isAdmin()) {
            navigate("/dashboard");
            return;
        }
    }, [isAdmin, navigate]);

    useEffect(() => {
        if (token) {
            checkAdminSettings(token).then(setSettingsCheck);
        }
    }, [token]);

    const [activeSection, setActiveSection] = useState<string>('');

    const sections = [
        { 
            id: 'organization', 
            label: t('adminSettings.organizationDetails.title'), 
            icon: Building,
            isMissing: settingsCheck ? !settingsCheck.hasOrganizationDetails : false
        },
        {
            id: 'email-config',
            label: t('adminSettings.emailConfig.title'),
            icon: Mail,
            isMissing: false
        },
        ...(emailCapabilityAvailable ? [{
            id: 'email-templates',
            label: t('adminSettings.emailTemplates.title'),
            icon: FileText,
            isMissing: false
        }] : []),
        {
            id: 'password-policy',
            label: t('adminSettings.passwordPolicy.title'),
            icon: Lock,
            isMissing: false
        },
        {
            id: 'password-blocklist',
            label: t('adminSettings.passwordBlocklist.title'),
            icon: Ban,
            isMissing: false
        },
        {
            id: 'mileage',
            label: t('adminSettings.mileageRate.title'),
            icon: Calculator,
            isMissing: settingsCheck ? !settingsCheck.hasMileageRate : false
        },
        {
            id: 'billing-unit',
            label: t('adminSettings.billingUnitSettings.title'),
            icon: Clock,
            isMissing: false,
        },
        {
            id: 'nda-reminder',
            label: t('adminSettings.ndaReminder.title'),
            icon: Bell,
            isMissing: false
        },
        {
            id: 'birthday-settings',
            label: t('adminSettings.birthdaySettings.title'),
            icon: Cake,
            isMissing: false
        },
        {
            id: 'member-address-prefill',
            label: t('adminSettings.memberAddressPrefill.title'),
            icon: MapPinned,
            isMissing: false
        },
        {
            id: 'social-account',
            label: t('adminSettings.socialAccount.title'),
            icon: PiggyBank,
            isMissing: false
        },
        { 
            id: 'membership-fees', 
            label: t('adminSettings.membershipFees.title'), 
            icon: CreditCard,
            isMissing: settingsCheck ? !settingsCheck.hasMembershipFeeTypes : false
        },
        {
            id: 'submission',
            label: t('adminSettings.submissionChannels.title'),
            icon: Send,
            isMissing: settingsCheck ? !settingsCheck.hasSubmissionChannels : false
        },
        {
            id: 'resignation-reasons',
            label: t('adminSettings.resignationReasons.title'),
            icon: UserMinus,
            isMissing: false
        },
        {
            id: 'mobility-options',
            label: t('adminSettings.mobilityOptions.title'),
            icon: Car,
            isMissing: settingsCheck ? !settingsCheck.hasMobilityOptions : false
        },
        {
            id: 'districts',
            label: t('adminSettings.districts.title'),
            icon: MapPin,
            isMissing: false
        },
        {
            id: 'pricing',
            label: t('adminSettings.pricingScheme.title'),
            icon: Euro,
            isMissing: settingsCheck ? !settingsCheck.hasActivePricingScheme : false
        },
        {
            id: 'import',
            label: t('adminSettings.import.title'),
            icon: Upload,
            isMissing: false
        },
        {
            id: 'data-cleanup',
            label: t('adminSettings.dataCleanup.title'),
            icon: ShieldCheck,
            isMissing: false
        },
        {
            id: 'exports',
            label: t('adminSettings.exports.title'),
            icon: Download,
            isMissing: false
        },
        {
            id: 'audit-log',
            label: t('adminSettings.auditLog.title'),
            icon: ScrollText,
            isMissing: false
        },
        {
            id: 'email-send-log',
            label: t('adminSettings.emailSendLog.title'),
            icon: Mail,
            isMissing: false
        }
    ];

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const offset = 100; // Account for fixed header
            const elementPosition = element.getBoundingClientRect().top + window.scrollY;
            const offsetPosition = elementPosition - offset;
            
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY + 150;
            
            for (const section of sections) {
                const element = document.getElementById(section.id);
                if (element) {
                    const { top, bottom } = element.getBoundingClientRect();
                    const elementTop = top + window.scrollY;
                    const elementBottom = bottom + window.scrollY;
                    
                    if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
                        setActiveSection(section.id);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Check initial position

        return () => window.removeEventListener('scroll', handleScroll);
    }, [sections]);

    // Early return after all hooks
    if (!isAdmin()) {
        return null;
    }

    return (
        <div className="flex gap-8">
            {/* Sticky Navigation Sidebar */}
            <div className="w-64 flex-shrink-0">
                <div className="sticky top-24">
                    <nav className="space-y-1">
                        <Text className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                            {t('adminSettings.navigation.title')}
                        </Text>
                        {sections.map((section) => {
                            const Icon = section.icon;
                            return (
                                <button
                                    key={section.id}
                                    onClick={() => scrollToSection(section.id)}
                                    className={`
                                        w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg
                                        transition-colors duration-200
                                        ${
                                            activeSection === section.id
                                                ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 font-medium'
                                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                                        }
                                    `}
                                >
                                    <div className="flex items-center gap-2 flex-1">
                                        <Icon className="h-4 w-4 flex-shrink-0" />
                                        <span className="text-left flex-1">{section.label}</span>
                                        {section.isMissing && (
                                            <span 
                                                title={t('adminSettings.missingConfiguration.tooltip')}
                                                aria-label={t('adminSettings.missingConfiguration.ariaLabel')}
                                            >
                                                <AlertCircle className="h-4 w-4 text-amber-500 dark:text-amber-400 flex-shrink-0" />
                                            </span>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
                <Heading>
                    {t("adminSettings.mainHeader")}
                </Heading>

                <Divider className="my-5" soft/>

                <div id="organization" className="scroll-mt-24">
                    <SectionOrganizationDetails isMissing={settingsCheck ? !settingsCheck.hasOrganizationDetails : false} />
                </div>

                <Divider className="my-8" soft/>

                <div id="email-config" className="scroll-mt-24">
                    <SectionEmailConfig />
                </div>

                {emailCapabilityAvailable && (
                    <>
                        <Divider className="my-8" soft/>

                        <div id="email-templates" className="scroll-mt-24">
                            <SectionEmailTemplates />
                        </div>
                    </>
                )}

                <Divider className="my-8" soft/>

                <div id="password-policy" className="scroll-mt-24">
                    <SectionPasswordPolicy />
                </div>

                <Divider className="my-8" soft/>

                <div id="password-blocklist" className="scroll-mt-24">
                    <SectionPasswordBlocklist />
                </div>

                <Divider className="my-8" soft/>

                <div id="mileage" className="scroll-mt-24">
                    <MileageRateSetting isMissing={settingsCheck ? !settingsCheck.hasMileageRate : false} />
                </div>

                <Divider className="my-8" soft/>

                <div id="billing-unit" className="scroll-mt-24">
                    <SectionBillingUnitSettings />
                </div>

                <Divider className="my-8" soft/>

                <div id="nda-reminder" className="scroll-mt-24">
                    <SectionNdaReminderSettings />
                </div>

                <Divider className="my-8" soft/>

                <div id="birthday-settings" className="scroll-mt-24">
                    <SectionBirthdaySettings />
                </div>

                <Divider className="my-8" soft/>

                <div id="member-address-prefill" className="scroll-mt-24">
                    <SectionMemberAddressPrefill />
                </div>

                <Divider className="my-8" soft/>

                <div id="social-account" className="scroll-mt-24">
                    <SectionSocialAccount />
                </div>

                <Divider className="my-8" soft/>

                <div id="membership-fees" className="scroll-mt-24">
                    <SectionMembershipFeeSettings isMissing={settingsCheck ? !settingsCheck.hasMembershipFeeTypes : false} />
                </div>

                <Divider className="my-8" soft/>

                <div id="submission" className="scroll-mt-24">
                    <SectionSubmissionChannel isMissing={settingsCheck ? !settingsCheck.hasSubmissionChannels : false} />
                </div>

                <Divider className="my-8" soft/>

                <div id="resignation-reasons" className="scroll-mt-24">
                    <SectionResignationReason />
                </div>

                <Divider className="my-8" soft/>

                <div id="mobility-options" className="scroll-mt-24">
                    <SectionMobilityOptions isMissing={settingsCheck ? !settingsCheck.hasMobilityOptions : false} />
                </div>

                <Divider className="my-8" soft/>

                <div id="districts" className="scroll-mt-24">
                    <SectionDistricts />
                </div>

                <Divider className="my-8" soft/>

                <div id="pricing" className="scroll-mt-24">
                    <SectionPricingConfiguration isMissing={settingsCheck ? !settingsCheck.hasActivePricingScheme : false} />
                </div>

                <Divider className="my-8" soft/>

                <div id="import" className="scroll-mt-24">
                    <SectionImportSettings />
                </div>

                <Divider className="my-8" soft/>

                <div id="data-cleanup" className="scroll-mt-24">
                    <SectionDataCleanup />
                </div>

                <Divider className="my-8" soft/>

                <div id="exports" className="scroll-mt-24">
                    <SectionExports />
                </div>

                <Divider className="my-8" soft/>

                <div id="audit-log" className="scroll-mt-24">
                    <SectionAuditLog />
                </div>

                <Divider className="my-8" soft/>

                <div id="email-send-log" className="scroll-mt-24">
                    <SectionEmailSendLog />
                </div>
            </div>
        </div>
    )
}
